// Review Order — final customer-facing screen before Square hosted
// checkout. Loads authoritative selected plan, collects pickup + trainer
// + consent, then invokes the trusted create-square-checkout Edge
// Function.
//
// Frontend is a thin renderer. Supabase RPCs are authoritative for
// validation and persistence:
//   - get_selected_meal_plan
//   - get_pickup_rules, select_pickup_time
//   - get_current_consent, accept_current_consent
//   - validate_trainer_code, apply_trainer_attribution,
//     clear_trainer_attribution
//   - create-square-checkout (Edge Function, wire body:
//     { checkout_request_id: <UUID> } — no other fields)
// React never fabricates prices, discounts, taxes, totals, or store
// hours; the RPCs' returned strings and outcomes are rendered as-is.

import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';
import { useSession } from '../lib/useSession';
import {
  rpcGetSelectedMealPlan,
  readSelectedPlan,
  hasSelectedPlan,
} from '../lib/planPersistence';
import { PLAN_LABEL_DISPLAY } from '../lib/mealPlanReader';
import {
  rpcGetPickupRules,
  rpcSelectPickupTime,
  readPickupMinLeadHours,
  readConfirmedPickupAt,
  readPickupOutcome,
  readStoreTimezone,
  readStoreHoursForDate,
  buildStoreZonedIso,
  fmtHumanInTz,
  readPersistedPickupFromPlanPayload,
  earliestPickupDateInTz,
} from '../lib/pickup';
import {
  rpcGetCurrentConsent,
  rpcAcceptCurrentConsent,
  readConsentText,
  readConsentVersion,
  readConsentAlreadyAccepted,
} from '../lib/consent';
import {
  rpcValidateTrainerCode,
  rpcApplyTrainerAttribution,
  rpcClearTrainerAttribution,
  readTrainerName,
  readTrainerBenefitText,
} from '../lib/trainer';
import {
  getOrCreateCheckoutRequestId,
  invokeCreateSquareCheckout,
} from '../lib/squareCheckout';

// -----------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------

function fmtInt(n) { return Number.isFinite(n) ? Math.round(n).toString() : '—'; }
function fmtDec1(n) { return Number.isFinite(n) ? (Math.round(n * 10) / 10).toString() : '—'; }

// Auto-format a raw pickup-time input into HH:mm as the customer types
// on a numeric-only mobile keypad (no ":" key available). Keeps only
// digits, caps at 4, and inserts the colon after the hour pair.
// pickupTime remains the single source of truth — no second state.
function formatPickupTimeInput(raw) {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

// Map controlled backend outcomes to customer-safe copy.
function mapPickupOutcomeToMessage(outcome) {
  switch (outcome) {
    case 'outside_hours':
    case 'outside_store_hours':
    case 'store_closed':
      return 'Please choose a pickup time during store hours.';
    case 'lead_time_violation':
    case 'too_soon':
      return 'Pickup must be scheduled at least 24 hours in advance.';
    case 'invalid_time':
    case 'invalid_pickup_at':
      return 'Please choose a valid pickup date and time.';
    case 'no_selected_plan':
      return 'Please select a meal plan before choosing a pickup time.';
    case 'not_authenticated':
      return 'Please sign in again to continue.';
    case 'unavailable':
    case 'capacity_full':
      return "That pickup time isn't available. Please choose another time.";
    default:
      return "That pickup time isn't available. Please choose another time.";
  }
}

// -----------------------------------------------------------------
// Item + day rendering
// -----------------------------------------------------------------

function ItemLine({ item }) {
  const isSide = item.role !== 'core';
  if (item.customer_included === false) return null;
  return (
    <div
      className="py-2 flex items-start justify-between gap-3"
      style={{ borderBottom: '1px solid var(--line)' }}
      data-testid={`review-item-${item.day_number}-${item.slot}-${item.component_order}`}
    >
      <div className="min-w-0" style={{ flex: '1 1 200px' }}>
        <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>
          {isSide ? `Add-on · ${item.role.replace('_', ' ')}` : `Meal ${item.slot}`}
        </div>
        <div className="leading-tight" style={{ color: 'var(--ink)', fontSize: isSide ? '0.95rem' : '1.05rem' }}>
          {isSide && '↳ '}
          {item.product_name || '—'}
          {item.quantity > 1 && (
            <span className="ml-2 text-sm" style={{ color: 'var(--text-dim)' }}>× {item.quantity}</span>
          )}
        </div>
        {item.serving_size && (
          <div className="mt-1 text-xs" style={{ color: 'var(--text-dim)' }}>Serving: {item.serving_size}</div>
        )}
      </div>
    </div>
  );
}

function DayBlock({ day }) {
  const visibleItems = day.items.filter((it) => it.customer_included !== false);
  return (
    <div className="mb-6" data-testid={`review-day-${day.day_number}`}>
      <h3 className="font-display text-lg" style={{ color: 'var(--ink)' }}>Day {day.day_number}</h3>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2"
        data-testid={`review-day-totals-${day.day_number}`}>
        <div>
          <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Calories</div>
          <div className="font-display text-lg">{fmtInt(day.totals.calories)}</div>
        </div>
        <div>
          <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Protein</div>
          <div className="font-display text-lg">{fmtDec1(day.totals.protein)} g</div>
        </div>
        <div>
          <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Carbs</div>
          <div className="font-display text-lg">{fmtDec1(day.totals.carbohydrates)} g</div>
        </div>
        <div>
          <div className="eyebrow" style={{ color: 'var(--text-dim)', fontSize: '10px', letterSpacing: '0.14em' }}>Fat</div>
          <div className="font-display text-lg">{fmtDec1(day.totals.fat)} g</div>
        </div>
      </div>
      <div className="mt-3" style={{ borderTop: '1px solid var(--line)' }}>
        {visibleItems.map((it) => (
          <ItemLine key={[it.day_number, it.slot, it.component_order, it.etm_id].join('-')} item={it} />
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Page
// -----------------------------------------------------------------

export default function ReviewOrder() {
  const { session, loading: sessionLoading, configured } = useSession();
  const nav = useNavigate();

  // Selected plan
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [globalError, setGlobalError] = useState(null);

  // Pickup
  const [pickupRules, setPickupRules] = useState(null);
  const [pickupRulesLoading, setPickupRulesLoading] = useState(true);
  const [pickupRulesError, setPickupRulesError] = useState(null);
  const [pickupRulesRetrying, setPickupRulesRetrying] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [confirmedPickupAt, setConfirmedPickupAt] = useState(null);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupError, setPickupError] = useState(null);

  // Consent
  const [consentPayload, setConsentPayload] = useState(null);
  const [consentText, setConsentText] = useState('');
  const [consentVersion, setConsentVersion] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState(null);

  // Trainer
  const [trainerCodeInput, setTrainerCodeInput] = useState('');
  const [appliedTrainerCode, setAppliedTrainerCode] = useState(null);
  const [appliedTrainerName, setAppliedTrainerName] = useState(null);
  const [appliedTrainerBenefit, setAppliedTrainerBenefit] = useState(null);
  const [trainerBusy, setTrainerBusy] = useState(false);
  const [trainerError, setTrainerError] = useState(null);

  // Place-order
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState(null);

  // ----- Load selected plan, pickup rules, and consent on mount. -----
  useEffect(() => {
    if (sessionLoading) return;
    if (!session) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setGlobalError(null);
    Promise.all([
      rpcGetSelectedMealPlan(),
      rpcGetPickupRules(),
      rpcGetCurrentConsent(),
    ]).then(([planR, pickupR, consentR]) => {
      if (cancelled) return;
      if (planR.ok && hasSelectedPlan(planR.data)) {
        setSelectedPlan(readSelectedPlan(planR.data));
        // Backend-authoritative rehydration of a previously confirmed
        // pickup: if the selected-plan payload exposes a persisted
        // requested_pickup_at, use it. We DO NOT trust sessionStorage
        // as a source of "confirmed" state.
        const persisted = readPersistedPickupFromPlanPayload(planR.data);
        if (persisted) setConfirmedPickupAt(persisted);
      } else if (!planR.ok) {
        setGlobalError(planR.error);
      }
      // Pickup rules: distinguish SUCCESS (parse payload into a rules
      // object) from RPC FAILURE (leave pickupRules null and surface a
      // retry state). Never fabricate an empty {} on failure — that
      // would falsely satisfy the disabled gate.
      applyPickupRulesResponse(pickupR);
      if (consentR.ok) {
        setConsentPayload(consentR.data);
        setConsentText(readConsentText(consentR.data) || '');
        setConsentVersion(readConsentVersion(consentR.data));
        if (readConsentAlreadyAccepted(consentR.data)) {
          setConsentAccepted(true);
          setConsentChecked(true);
        }
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [session, sessionLoading]);

  // Apply a get_pickup_rules response. Accepts the object shape, the
  // one-row top-level array shape, or wraps `{ data: {...} }`. RPC
  // failure is preserved as an explicit error state — pickupRules
  // stays null so the Confirm gate remains blocked and the customer
  // is offered Retry.
  function applyPickupRulesResponse(pickupR) {
    setPickupRulesLoading(false);
    if (pickupR && pickupR.ok) {
      const d = pickupR.data;
      let obj = null;
      if (Array.isArray(d)) obj = d[0] || null;
      else if (d && typeof d === 'object' && !Array.isArray(d)) obj = d;
      // Success even if the returned object is empty — the server is
      // the authority and may return a rules-less payload.
      setPickupRules(obj || {});
      setPickupRulesError(null);
    } else {
      // RPC actually failed. Do NOT fabricate an empty rules object.
      setPickupRules(null);
      setPickupRulesError(pickupR?.error || 'We couldn\u2019t load pickup availability. Please try again.');
    }
  }

  async function retryPickupRules() {
    if (pickupRulesRetrying) return;
    setPickupRulesRetrying(true);
    setPickupRulesLoading(true);
    setPickupRulesError(null);
    const r = await rpcGetPickupRules();
    applyPickupRulesResponse(r);
    setPickupRulesRetrying(false);
  }

  const minLeadHours = readPickupMinLeadHours(pickupRules);
  const storeTz = readStoreTimezone(pickupRules);
  const earliestDate = useMemo(
    () => earliestPickupDateInTz(minLeadHours, storeTz),
    [minLeadHours, storeTz]
  );
  const hoursForSelectedDate = useMemo(
    () => (pickupDate ? readStoreHoursForDate(pickupRules, pickupDate) : null),
    [pickupRules, pickupDate]
  );

  // ----- Pickup handlers ----------------------------------------------
  async function handleConfirmPickup() {
    setPickupError(null);
    // Single source of truth: controlled React state. The visible
    // input values and the values submitted here are always the
    // same `pickupDate` / `pickupTime` variables.
    const dateVal = pickupDate;
    const timeVal = pickupTime;
    if (!dateVal || !timeVal) {
      setPickupError('Please choose a date and time.');
      return;
    }
    // Basic HH:mm (00:00 – 23:59) format check for the plain-text
    // time control. Backend remains authoritative on business hours.
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(timeVal)) {
      setPickupError('Please enter a valid time in 24-hour HH:mm format (e.g. 14:30).');
      return;
    }
    // Guidance (server still authoritative): reject an obviously
    // out-of-hours time before the RPC round trip.
    const hoursForClickedDate = readStoreHoursForDate(pickupRules, dateVal);
    if (hoursForClickedDate) {
      if (hoursForClickedDate.closed) {
        setPickupError('The store is closed on that day. Please choose another date.');
        return;
      }
      const { open, close } = hoursForClickedDate;
      if (open && timeVal < open) {
        setPickupError(`Please choose a pickup time during store hours (opens ${open}).`);
        return;
      }
      if (close && timeVal >= close) {
        setPickupError(`Please choose a pickup time during store hours (closes ${close}).`);
        return;
      }
    }
    const iso = buildStoreZonedIso(dateVal, timeVal, storeTz);
    if (!iso) {
      setPickupError('Please choose a valid date and time.');
      return;
    }
    setPickupBusy(true);
    const r = await rpcSelectPickupTime(iso);
    setPickupBusy(false);
    if (!r.ok) { setPickupError(r.error); return; }

    // Only outcome === 'selected' counts as a confirmed pickup.
    const outcome = readPickupOutcome(r.data);
    if (outcome !== 'selected') {
      setPickupError(mapPickupOutcomeToMessage(outcome));
      return;
    }
    // Prefer the confirmed value the server echoes back; fall back to
    // the ISO we sent if the RPC returns nothing further.
    const confirmed = readConfirmedPickupAt(r.data) || iso;
    setConfirmedPickupAt(confirmed);
  }

  function handleChangePickup() {
    // Allow the customer to change their pickup. Pre-populate the
    // inputs with the previous confirmed values so they can adjust
    // rather than re-enter, and ensure React state reflects that.
    // Clearing confirmedPickupAt automatically rotates the checkout
    // UUID (fingerprint-keyed).
    if (confirmedPickupAt) {
      try {
        const dt = new Date(confirmedPickupAt);
        // Format using the STORE timezone so the customer sees the
        // wall-clock they originally chose.
        const dtf = new Intl.DateTimeFormat('en-CA', {
          timeZone: storeTz || undefined,
          year: 'numeric', month: '2-digit', day: '2-digit',
        });
        const dateParts = {};
        for (const p of dtf.formatToParts(dt)) if (p.type !== 'literal') dateParts[p.type] = p.value;
        const tf = new Intl.DateTimeFormat('en-GB', {
          timeZone: storeTz || undefined,
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
        const timeStr = tf.format(dt);
        if (dateParts.year && dateParts.month && dateParts.day) {
          setPickupDate(`${dateParts.year}-${dateParts.month}-${dateParts.day}`);
        }
        if (/^\d{2}:\d{2}$/.test(timeStr)) {
          setPickupTime(timeStr);
        }
      } catch { /* ignore parse errors */ }
    }
    setConfirmedPickupAt(null);
    setPickupError(null);
  }

  // ----- Consent handlers --------------------------------------------
  async function handleAcceptConsent() {
    if (!consentChecked) return;
    setConsentError(null);
    setConsentBusy(true);
    const r = await rpcAcceptCurrentConsent();
    setConsentBusy(false);
    if (!r.ok) { setConsentError(r.error); return; }
    setConsentAccepted(true);
  }

  // ----- Trainer handlers --------------------------------------------
  async function handleApplyTrainer() {
    setTrainerError(null);
    const code = (trainerCodeInput || '').trim();
    if (!code) { setTrainerError('Please enter a trainer code.'); return; }
    setTrainerBusy(true);
    const v = await rpcValidateTrainerCode(code);
    if (!v.ok) { setTrainerBusy(false); setTrainerError(v.error); return; }
    const a = await rpcApplyTrainerAttribution(code);
    setTrainerBusy(false);
    if (!a.ok) { setTrainerError(a.error); return; }
    setAppliedTrainerCode(code);
    setAppliedTrainerName(readTrainerName(v.data) || readTrainerName(a.data));
    setAppliedTrainerBenefit(readTrainerBenefitText(v.data) || readTrainerBenefitText(a.data));
    setTrainerCodeInput('');
  }

  async function handleClearTrainer() {
    setTrainerError(null);
    setTrainerBusy(true);
    const r = await rpcClearTrainerAttribution();
    setTrainerBusy(false);
    if (!r.ok) { setTrainerError(r.error); return; }
    setAppliedTrainerCode(null);
    setAppliedTrainerName(null);
    setAppliedTrainerBenefit(null);
  }

  // ----- Place order --------------------------------------------------
  const canPlace = Boolean(
    selectedPlan && selectedPlan.plan_id &&
    confirmedPickupAt &&
    consentAccepted &&
    !placing
  );

  async function handlePlaceOrder() {
    if (!canPlace) return;
    setPlaceError(null);
    setPlacing(true);
    // State-fingerprinted idempotency: same plan+pickup+trainer+consent
    // → same UUID; any change → fresh UUID.
    const { id: checkoutRequestId } = getOrCreateCheckoutRequestId({
      plan_id: selectedPlan.plan_id,
      pickup_at: confirmedPickupAt,
      trainer_code: appliedTrainerCode,
      consent_version: consentVersion,
    });
    const r = await invokeCreateSquareCheckout(checkoutRequestId);
    if (!r.ok) {
      setPlaceError(r.error);
      setPlacing(false);
      return;
    }
    // Full-page navigation to Square. Server will redirect back via
    // SQUARE_CHECKOUT_REDIRECT_URL (order_id appended) — our
    // OrderStatus page handles that return.
    window.location.assign(r.data.checkout_url);
  }

  // ----- Guards -------------------------------------------------------
  if (!configured) {
    return (
      <AppShell>
        <div className="wizard-card">
          <div className="eyebrow">Review order</div>
          <h1 className="h2 mt-3">Checkout is temporarily unavailable</h1>
          <p className="mt-4" style={{ color: 'var(--text-dim)' }}>Please try again in a moment.</p>
        </div>
      </AppShell>
    );
  }
  if (sessionLoading || loading) {
    return (
      <AppShell>
        <div className="wizard-card"><p style={{ color: 'var(--text-dim)' }}>Loading your order…</p></div>
      </AppShell>
    );
  }
  if (!session) return <Navigate to="/sign-in" replace state={{ returnTo: '/plan/review' }} />;
  if (!selectedPlan) return <Navigate to="/plan/results" replace />;

  const displayLabel =
    PLAN_LABEL_DISPLAY[selectedPlan.option_label] || selectedPlan.option_label || 'Your plan';

  // ----- Render -------------------------------------------------------
  return (
    <AppShell>
      <div className="wizard-card" data-testid="review-order">
        <div className="eyebrow">Review order</div>
        <h1 className="h2 mt-3" data-testid="review-title">Review your order</h1>
        <p className="mt-3" style={{ color: 'var(--text-dim)' }}>
          Confirm your pickup, add an optional trainer code, agree to the
          terms, and continue to secure checkout.
        </p>

        {globalError && (
          <div className="mt-4 p-3"
            style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
            data-testid="review-global-error">
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{globalError}</p>
          </div>
        )}

        {/* Selected plan summary --------------------------------- */}
        <section className="mt-6 p-4"
          style={{ border: '2px solid var(--orange, #d34a1e)', borderRadius: 'var(--radius)', background: 'var(--paper)' }}
          data-testid="review-plan-card">
          <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Your selected plan</div>
          <h2 className="font-display text-2xl leading-tight mt-1"
            style={{ color: 'var(--ink)' }} data-testid="review-plan-name">
            {displayLabel}
          </h2>
          {selectedPlan.explanation && (
            <p className="mt-2 text-sm" style={{ color: 'var(--ink-2)' }}
              data-testid="review-plan-explanation">{selectedPlan.explanation}</p>
          )}
          <div className="mt-6" data-testid="review-days">
            {selectedPlan.days.map((day) => (<DayBlock key={day.day_number} day={day} />))}
          </div>
          <div className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}>
            Plan reference: <span data-testid="review-plan-id">{selectedPlan.plan_id}</span>
          </div>
        </section>

        {/* Pickup ------------------------------------------------- */}
        <section className="mt-8" data-testid="review-pickup-section">
          <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Pickup</div>
          <h2 className="font-display text-xl leading-tight mt-1" style={{ color: 'var(--ink)' }}>
            When will you pick up your meals?
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-dim)' }}
            data-testid="review-pickup-24h-notice">
            Pickup requires a minimum of 24 hours from the time your order is placed.
          </p>
          {Number.isFinite(minLeadHours) && (
            <p className="mt-2 text-sm" style={{ color: 'var(--text-dim)' }}>
              Please give us at least {minLeadHours} hours&rsquo; notice.
            </p>
          )}

          {/* Rules loading / failure banner (Phase B11):
              While rules are loading, or when the RPC failed, the
              picker is not authoritatively usable. Show a clear
              customer-safe state and a Retry that re-invokes the
              existing get_pickup_rules() RPC. */}
          {pickupRulesLoading && !pickupRules && (
            <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }}
              data-testid="review-pickup-rules-loading">
              Loading pickup availability…
            </p>
          )}
          {!pickupRulesLoading && !pickupRules && pickupRulesError && (
            <div className="mt-3 p-3"
              style={{
                background: 'var(--warning-bg, rgba(178,106,0,0.12))',
                border: '1px solid var(--warning, #b26a00)',
                borderRadius: 'var(--radius)',
              }}
              data-testid="review-pickup-rules-error">
              <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
                We couldn&rsquo;t load pickup availability. Please try again.
              </p>
              <div className="mt-2">
                <BrandButton
                  type="button" variant="outline" size="sm"
                  onClick={retryPickupRules}
                  disabled={pickupRulesRetrying}
                  data-testid="review-pickup-rules-retry-btn"
                >
                  {pickupRulesRetrying ? 'Retrying…' : 'Retry'}
                </BrandButton>
              </div>
            </div>
          )}

          {confirmedPickupAt ? (
            <div className="mt-3 p-3"
              style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }}
              data-testid="review-pickup-confirmed">
              <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Confirmed pickup</div>
              <div className="font-display text-lg" data-testid="review-pickup-confirmed-value">
                {fmtHumanInTz(confirmedPickupAt, storeTz)}
              </div>
              <div className="mt-2">
                <button type="button" className="text-sm underline"
                  style={{ color: 'var(--ink-2)' }}
                  onClick={handleChangePickup}
                  data-testid="review-pickup-change-btn">
                  Change pickup time
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <div className="flex flex-wrap gap-3 items-end">
                <label className="flex flex-col text-sm" style={{ color: 'var(--ink-2)' }}>
                  <span className="eyebrow mb-1" style={{ color: 'var(--text-dim)' }}>Date</span>
                  <input
                    type="date"
                    value={pickupDate}
                    min={earliestDate}
                    onChange={(e) => { setPickupDate(e.target.value); if (pickupError) setPickupError(null); }}
                    style={{
                      padding: '0.55rem 0.75rem',
                      border: '1.5px solid var(--line)',
                      borderRadius: 'var(--radius)',
                      background: 'var(--paper)',
                      color: 'var(--ink)',
                      fontFamily: 'inherit',
                      fontSize: '1rem',
                      minWidth: 180,
                    }}
                    data-testid="review-pickup-date-input"
                  />
                </label>
                <label className="flex flex-col text-sm" style={{ color: 'var(--ink-2)' }}>
                  <span className="eyebrow mb-1" style={{ color: 'var(--text-dim)' }}>
                    Time (24-hour, HH:mm)
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="14:30"
                    maxLength={5}
                    value={pickupTime}
                    onChange={(e) => { setPickupTime(formatPickupTimeInput(e.target.value)); if (pickupError) setPickupError(null); }}
                    style={{
                      padding: '0.55rem 0.75rem',
                      border: '1.5px solid var(--line)',
                      borderRadius: 'var(--radius)',
                      background: 'var(--paper)',
                      color: 'var(--ink)',
                      fontFamily: 'inherit',
                      fontSize: '1rem',
                      minWidth: 160,
                      letterSpacing: '0.05em',
                    }}
                    data-testid="review-pickup-time-input"
                  />
                </label>
                <BrandButton
                  type="button"
                  variant="ink"
                  size="sm"
                  onClick={handleConfirmPickup}
                  disabled={pickupBusy || pickupRulesLoading || !pickupRules}
                  data-testid="review-pickup-confirm-btn"
                >
                  {pickupBusy ? 'Checking…' : 'Confirm pickup'}
                </BrandButton>
              </div>
              {hoursForSelectedDate && !hoursForSelectedDate.closed && (hoursForSelectedDate.open || hoursForSelectedDate.close) && (
                <p className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}
                  data-testid="review-pickup-hours-hint">
                  Store hours that day: {hoursForSelectedDate.open || '—'} – {hoursForSelectedDate.close || '—'}
                  {storeTz ? ` (${storeTz})` : ''}
                </p>
              )}
              {hoursForSelectedDate && hoursForSelectedDate.closed && (
                <p className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}
                  data-testid="review-pickup-hours-hint">
                  The store is closed on that day. Please choose another date.
                </p>
              )}
            </div>
          )}
          {pickupError && (
            <p className="mt-2 text-sm" style={{ color: 'var(--danger, #b03a2e)' }}
              data-testid="review-pickup-error">{pickupError}</p>
          )}
        </section>

        {/* Trainer code (optional) ------------------------------- */}
        <section className="mt-8" data-testid="review-trainer-section">
          <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Trainer code (optional)</div>
          <h2 className="font-display text-xl leading-tight mt-1" style={{ color: 'var(--ink)' }}>
            Working with a trainer?
          </h2>

          {appliedTrainerCode ? (
            <div className="mt-3 p-3"
              style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }}
              data-testid="review-trainer-applied">
              <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>Applied</div>
              <div className="font-display text-lg" data-testid="review-trainer-name">
                {appliedTrainerName || appliedTrainerCode}
              </div>
              {appliedTrainerBenefit && (
                <div className="text-sm mt-1" style={{ color: 'var(--ink-2)' }}
                  data-testid="review-trainer-benefit">{appliedTrainerBenefit}</div>
              )}
              <div className="mt-2">
                <button type="button" className="text-sm underline"
                  style={{ color: 'var(--ink-2)' }}
                  onClick={handleClearTrainer}
                  disabled={trainerBusy}
                  data-testid="review-trainer-clear-btn">
                  {trainerBusy ? 'Working…' : 'Remove trainer code'}
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-3 items-end">
              <label className="flex flex-col text-sm" style={{ color: 'var(--ink-2)' }}>
                <span className="eyebrow mb-1" style={{ color: 'var(--text-dim)' }}>Trainer code</span>
                <input
                  type="text"
                  value={trainerCodeInput}
                  onChange={(e) => setTrainerCodeInput(e.target.value)}
                  placeholder="e.g. SAMPLE-CODE"
                  className="btn btn-outline"
                  style={{ padding: '0.6rem 0.8rem', minWidth: 220, textTransform: 'uppercase' }}
                  data-testid="review-trainer-input"
                />
              </label>
              <BrandButton
                type="button"
                variant="ink"
                size="sm"
                onClick={handleApplyTrainer}
                disabled={trainerBusy || !trainerCodeInput.trim()}
                data-testid="review-trainer-apply-btn"
              >
                {trainerBusy ? 'Checking…' : 'Apply code'}
              </BrandButton>
            </div>
          )}
          {trainerError && (
            <p className="mt-2 text-sm" style={{ color: 'var(--danger, #b03a2e)' }}
              data-testid="review-trainer-error">{trainerError}</p>
          )}
        </section>

        {/* Consent ---------------------------------------------- */}
        <section className="mt-8" data-testid="review-consent-section">
          <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>
            Terms {consentVersion ? `· v${consentVersion}` : ''}
          </div>
          <h2 className="font-display text-xl leading-tight mt-1" style={{ color: 'var(--ink)' }}>
            Please read and agree
          </h2>

          <div className="mt-3 p-3"
            style={{
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius)',
              background: 'var(--paper)',
              maxHeight: 260, overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: '0.95rem', color: 'var(--ink-2)',
            }}
            data-testid="review-consent-text">
            {consentText || 'Loading terms…'}
          </div>

          <label className="mt-3 flex items-start gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              disabled={consentAccepted}
              data-testid="review-consent-checkbox"
            />
            <span>I have read and agree to the terms above.</span>
          </label>

          {!consentAccepted ? (
            <div className="mt-3">
              <BrandButton
                type="button"
                variant="ink"
                size="sm"
                onClick={handleAcceptConsent}
                disabled={!consentChecked || consentBusy || !consentText}
                data-testid="review-consent-accept-btn"
              >
                {consentBusy ? 'Recording…' : 'Agree and continue'}
              </BrandButton>
            </div>
          ) : (
            <div className="mt-3 text-sm" style={{ color: 'var(--success, #2e7d32)' }}
              data-testid="review-consent-accepted">
              Thanks — your acceptance is on file{consentVersion ? ` (v${consentVersion})` : ''}.
            </div>
          )}
          {consentError && (
            <p className="mt-2 text-sm" style={{ color: 'var(--danger, #b03a2e)' }}
              data-testid="review-consent-error">{consentError}</p>
          )}
        </section>

        {/* Place order ----------------------------------------- */}
        <p className="mt-8 text-sm" style={{ color: 'var(--text-dim)' }}>
          Pricing, taxes, and any trainer benefits are confirmed on the
          secure Square checkout page.
        </p>

        {placeError && (
          <div className="mt-3 p-3"
            style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}
            data-testid="review-place-error">
            <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{placeError}</p>
          </div>
        )}

        <div className="mt-4 flex gap-3 flex-wrap" data-testid="review-actions">
          <BrandButton
            type="button"
            variant="primary"
            onClick={handlePlaceOrder}
            disabled={!canPlace}
            data-testid="review-place-order-btn"
          >
            {placing ? 'Preparing checkout…' : 'Place order'}
          </BrandButton>
          <BrandButton
            type="button"
            variant="outline"
            onClick={() => nav('/plan/results')}
            disabled={placing}
            data-testid="review-back-btn"
          >
            Back to my plan
          </BrandButton>
        </div>

        {!canPlace && !placing && (
          <p className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}
            data-testid="review-place-hint">
            Confirm your pickup time and agree to the terms to continue.
          </p>
        )}
      </div>
    </AppShell>
  );
}
